import React, { useMemo } from 'react';
import Select from 'react-select';
import CategoryDropdown from '../../../shared/common-dropdown/CategoryDropdown';
import SubCategoryDropdown from '../../../shared/common-dropdown/SubCategoryDropdown';
import VendorDropdown from '../../../shared/common-dropdown/VendorDropdown';
import ProductHomePageSectionDropdown from '../../../shared/common-dropdown/ProductHomePageSectionDropdown';
import { getBadgeClassForLabel, getBadgeSelectOptions, slugify } from './utils/productHelpers';

function BooleanToggle({ id, label, checked, onChange, disabled }) {
  return (
    <div className="mb-3">
      <label className="form-label d-block">{label}</label>
      <div className="status-toggle modal-status">
        <input
          type="checkbox"
          id={id}
          className="check"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} className="checktoggle" />
      </div>
    </div>
  );
}

function ProductDetailsFields({ formData, sendDataToParent, readOnly = false, slugManual = false, onSlugManualChange }) {
  const propagate = (next) => {
    if (sendDataToParent) sendDataToParent(next);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };
    if (name === 'ProductName' && !slugManual) {
      next.Slug = slugify(value);
    }
    propagate(next);
  };

  const handleToggle = (name, checked) => {
    propagate({ ...formData, [name]: checked });
  };

  const onCategoryChange = (option) => {
    propagate({
      ...formData,
      CategoryId: option ? Number(option.value) : null,
      CategoryName: option?.label || '',
      SubCategoryId: null,
      SubCategoryName: '',
    });
  };

  const onSubCategoryChange = (option) => {
    propagate({
      ...formData,
      SubCategoryId: option ? Number(option.value) : null,
      SubCategoryName: option?.label || '',
    });
  };

  const onVendorChange = (option) => {
    propagate({
      ...formData,
      VendorId: option ? Number(option.value) : null,
      VendorName: option?.label || '',
    });
  };

  const onHomePageSectionChange = (option) => {
    propagate({
      ...formData,
      ProductHomePageSectionId: option ? Number(option.value) : null,
      SectionName: option?.label || '',
    });
  };

  const badgeOptions = useMemo(
    () => getBadgeSelectOptions(formData.BadgeLabel),
    [formData.BadgeLabel],
  );

  const selectedBadgeOption = useMemo(() => {
    const label = String(formData.BadgeLabel || '').trim();
    if (!label) return null;
    return badgeOptions.find((option) => option.value === label) || { value: label, label };
  }, [badgeOptions, formData.BadgeLabel]);

  const onBadgeChange = (option) => {
    const badgeLabel = option?.value || '';
    propagate({
      ...formData,
      BadgeLabel: badgeLabel,
      BadgeClass: badgeLabel ? (getBadgeClassForLabel(badgeLabel) || formData.BadgeClass || '') : '',
    });
  };

  if (readOnly) {
    return (
      <div className="row">
        <div className="col-lg-12 mb-2"><strong>Product Name:</strong> {formData.ProductName}</div>
        <div className="col-lg-6 mb-2"><strong>Category:</strong> {formData.CategoryName || '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Sub Category:</strong> {formData.SubCategoryName || '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Vendor:</strong> {formData.VendorName || '-'}</div>
        <div className="col-lg-6 mb-2"><strong>Slug:</strong> {formData.Slug}</div>
        <div className="col-lg-4 mb-2"><strong>Price:</strong> {formData.Price}</div>
        <div className="col-lg-4 mb-2"><strong>Old Price:</strong> {formData.OldPrice || '-'}</div>
        <div className="col-lg-4 mb-2"><strong>Rating:</strong> {formData.Rating || '-'}</div>
        <div className="col-lg-4 mb-2"><strong>Badge:</strong> {formData.BadgeLabel || '-'}</div>
        <div className="col-lg-3 mb-2"><strong>Brand:</strong> {formData.BrandName || '-'}</div>
        <div className="col-lg-3 mb-2"><strong>Homepage Section:</strong> {formData.SectionName || '-'}</div>
        <div className="col-lg-3 mb-2"><strong>Homepage Section Order:</strong> {formData.HomepageSectionOrder ?? 0}</div>
        <div className="col-lg-3 mb-2"><strong>Display Order:</strong> {formData.DisplayOrder ?? 0}</div>
        <div className="col-lg-6 mb-2"><strong>Free Delivery:</strong> {formData.IsFreeDelivery ? 'Yes' : 'No'}</div>
        <div className="col-lg-6 mb-2"><strong>Active:</strong> {formData.IsActive ? 'Yes' : 'No'}</div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="mb-3">
          <label className="form-label">Product Name<span style={{ color: 'red' }}>*</span></label>
          <input type="text" className="form-control" name="ProductName" value={formData.ProductName || ''} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-6">
        <CategoryDropdown
          selectedValue={formData.CategoryId}
          onChangeDropdown={onCategoryChange}
          required
          isClearable
        />
      </div>
      <div className="col-lg-6">
        <SubCategoryDropdown
          categoryId={formData.CategoryId}
          selectedValue={formData.SubCategoryId}
          onChangeDropdown={onSubCategoryChange}
          required
          isClearable
        />
      </div>
      <div className="col-lg-6">
        <VendorDropdown
          selectedValue={formData.VendorId}
          onChangeDropdown={onVendorChange}
          isClearable
        />
      </div>
      <div className="col-lg-6">
        <div className="mb-3">
          <label className="form-label">Slug<span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            className="form-control"
            name="Slug"
            value={formData.Slug || ''}
            onChange={(e) => {
              if (onSlugManualChange) onSlugManualChange(true);
              handleChange(e);
            }}
          />
        </div>
      </div>
      <div className="col-lg-3 col-md-3">
        <div className="mb-3">
          <label className="form-label">Price<span style={{ color: 'red' }}>*</span></label>
          <input type="number" step="0.01" className="form-control" name="Price" value={formData.Price ?? ''} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-3 col-md-3">
        <div className="mb-3">
          <label className="form-label">Old Price</label>
          <input type="number" step="0.01" className="form-control" name="OldPrice" value={formData.OldPrice ?? ''} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-3 col-md-3">
        <div className="mb-3">
          <label className="form-label">Rating</label>
          <input type="number" step="0.1" min="0" max="5" className="form-control" name="Rating" value={formData.Rating ?? ''} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-3 col-md-3">
        <div className="mb-3">
          <label className="form-label">Badge Label</label>
          <Select
            classNamePrefix="sk-product-badge"
            name="BadgeLabel"
            value={selectedBadgeOption}
            options={badgeOptions}
            onChange={onBadgeChange}
            isClearable
            menuPlacement="auto"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="mb-3">
          <label className="form-label">Brand Name</label>
          <input type="text" className="form-control" name="BrandName" value={formData.BrandName || ''} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="mb-3">
          <ProductHomePageSectionDropdown
            selectedValue={formData.ProductHomePageSectionId}
            onChangeDropdown={onHomePageSectionChange}
            isClearable
          />
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="mb-3">
          <label className="form-label">Homepage Section Order</label>
          <input type="number" className="form-control" name="HomepageSectionOrder" value={formData.HomepageSectionOrder ?? 0} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-3 col-md-6">
        <div className="mb-3">
          <label className="form-label">Display Order</label>
          <input type="number" className="form-control" name="DisplayOrder" value={formData.DisplayOrder ?? 0} onChange={handleChange} />
        </div>
      </div>
      <div className="col-lg-6">
        <BooleanToggle
          id={`IsFreeDelivery-${formData.ProductId || 'new'}`}
          label="Free Delivery"
          checked={formData.IsFreeDelivery}
          onChange={(v) => handleToggle('IsFreeDelivery', v)}
        />
      </div>
      <div className="col-lg-6">
        <BooleanToggle
          id={`IsActive-${formData.ProductId || 'new'}`}
          label="Active"
          checked={formData.IsActive}
          onChange={(v) => handleToggle('IsActive', v)}
        />
      </div>
    </div>
  );
}

export default ProductDetailsFields;
